import { Page } from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { MyDirectReports } from "@/pages/talent/my-feedback/components/MyDirectReports.tsx";
import { MyFeedbackAboutMe } from "@/pages/talent/my-feedback/components/MyFeedbackAboutMe.tsx";
import { MyFeedbackRespond } from "@/pages/talent/my-feedback/components/MyFeedbackRespond.tsx";
import { MyFeedbacksLeft } from "@/pages/talent/my-feedback/components/MyFeedbacksLeft.tsx";
import { MyReviewsTeam } from "@/pages/talent/my-feedback/components/MyReviewsTeam.tsx";
import {
  useMyEmployeePerformanceReviews,
  useMyEmployeePerformanceReviewsAwaitingReviewerInvite,
} from "@/queries/employees/use-employee-performance-review.query.ts";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

enum MyFeedbackSection {
  requests = "requests",
  feedbackAboutMe = "feedback-about-me",
  feedbacksLeft = "feedbacks-left",
  myDirectReports = "my-direct-reports",
  myTeam = "my-team",
}

export const MyFeedbackIndex = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { section } = useParams();

  const currentSection = useMemo(() => {
    if (
      section === MyFeedbackSection.requests ||
      section === MyFeedbackSection.feedbackAboutMe ||
      section === MyFeedbackSection.feedbacksLeft ||
      section === MyFeedbackSection.myDirectReports ||
      section === MyFeedbackSection.myTeam
    ) {
      return section;
    }
    return MyFeedbackSection.requests;
  }, [section]);

  useEffect(() => {
    if (section !== currentSection) {
      navigate(`/my-feedback/${currentSection}`, { replace: true });
    }
  }, [currentSection, navigate, section]);

  const currentTitle = useMemo(() => {
    switch (currentSection) {
      case MyFeedbackSection.feedbackAboutMe:
        return t("performance_review:feedback_about_me");
      case MyFeedbackSection.feedbacksLeft:
        return t("performance_review:feedback_ive_given");
      case MyFeedbackSection.myDirectReports:
        return t("performance_review:feedback_on_my_direct_reports");
      case MyFeedbackSection.myTeam:
        return t("performance_review:feedback_on_my_subordinates");
      case MyFeedbackSection.requests:
      default:
        return t("common:requests");
    }
  }, [currentSection, t]);

  useTitle(currentTitle);

  const isRequestsSection = currentSection === MyFeedbackSection.requests;

  const isTableSection =
    currentSection === MyFeedbackSection.feedbacksLeft ||
    currentSection === MyFeedbackSection.myTeam;

  const { data: reviewsRespond, isLoading: isReviewsRespondLoading } =
    useMyEmployeePerformanceReviews(isRequestsSection);

  const { data: reviewsInvite, isLoading: isReviewsInviteLoading } =
    useMyEmployeePerformanceReviewsAwaitingReviewerInvite(isRequestsSection);

  const renderSection = () => {
    switch (currentSection) {
      case MyFeedbackSection.feedbackAboutMe:
        return <MyFeedbackAboutMe />;
      case MyFeedbackSection.feedbacksLeft:
        return <MyFeedbacksLeft />;
      case MyFeedbackSection.myDirectReports:
        return <MyDirectReports />;
      case MyFeedbackSection.myTeam:
        return <MyReviewsTeam />;
      case MyFeedbackSection.requests:
      default:
        return (
          <MyFeedbackRespond
            reviewsRespond={reviewsRespond ?? []}
            reviewsInvite={reviewsInvite ?? []}
            isLoading={isReviewsRespondLoading || isReviewsInviteLoading}
          />
        );
    }
  };

  return (
    <Page className={isTableSection ? "h-full" : undefined}>
      <Page.Header title={currentTitle} />
      <Page.Content stretch={isTableSection}>{renderSection()}</Page.Content>
    </Page>
  );
};
